import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Survey, SurveyEntry, SurveyEntryInputType, SurveyEntryType } from 'src/app/services/jsonrpc/cms-server.service';
import { CmsService } from 'src/app/services/cms.service';
import { NotificationsService } from 'src/app/services/notifications.service';

interface InputType {
  type: SurveyEntryType;
  input: SurveyEntryInputType;
  display?: string;
}

@Component({
  selector: 'app-survey-edit',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss']
})
export class SurveyEditComponent implements OnInit {
  public types: InputType[] = [
    { type: SurveyEntryType.Header, input: null, display: 'Header (no user input)' },
    { type: SurveyEntryType.Input, input: SurveyEntryInputType.Text, display: 'Free Text' },
    { type: SurveyEntryType.Input, input: SurveyEntryInputType.Number, display: 'Number' },
    { type: SurveyEntryType.Input, input: SurveyEntryInputType.Scale, display: 'Choices' }
  ];

  public survey: Survey;
  public createNew: boolean;
  public loading = true;
  public exists = false;
  public hasChanged = false;

  public get entries(): SurveyEntry[] {
    return this.survey?.setup?.entries;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cms: CmsService,
    private modal: NgbModal,
    private notification: NotificationsService
  ) {}

  public ngOnInit() {
    this.loading = true;
    this.route.paramMap.subscribe(map => {
      const uuid = map.get('uuid');
      this.hasChanged = false;
      if (uuid === 'new') {
        this.survey = {
          title: 'New Survey (click to change name)',
          setup: {
            subtitle: 'Survey description (click to change)',
            entries: []
          }
        };
        this.exists = true;
        this.createNew = true;
        this.loading = false;
      } else {
        return this.cms.cms.surveyGet(uuid).then(s => {
          this.survey = s;
          this.exists = s?.uuid != null;
          this.createNew = false;
          this.loading = false;
        });
      }
    });
  }

  public async updateTitle(value: string) {
    this.survey.title = value;
    await this.update();
  }

  public async updateSubtitle(value: string) {
    this.survey.setup.subtitle = value;
    await this.update();
  }

  public async updateStartDate(value: Date) {
    this.survey.start = value?.toISOString();
    await this.update();
  }

  public async updateEndDate(value: Date) {
    this.survey.end = value?.toISOString();
    await this.update();
  }

  public async update() {
    if (this.createNew) {
      this.hasChanged = true;
    } else {
      await this.cms.cms
        .surveyUpdate(this.survey)
        .then(() => this.notification.notify('success', 'Updated', 'The survey was successfully updated.'));
    }
  }

  public async save() {
    if (this.createNew) {
      await this.cms.cms.surveyCreate(this.survey).then(s => this.router.navigate(['/survey', s.uuid]));
    } else {
      await this.update().then(() => (this.hasChanged = false));
    }
  }

  public getSelectedType(e: SurveyEntry): InputType {
    return this.types.find(t => t.type == e.type && t.input == e.input);
  }

  public setSelectedType(e: SurveyEntry, display: any) {
    const type = this.types.find(t => t.display == display);
    if (type && type != this.getSelectedType(e)) {
      e.type = type.type;
      e.input = type.input;
      this.hasChanged = true;
    }
  }

  public addEntry() {
    this.entries.push({
      type: SurveyEntryType.Input,
      input: SurveyEntryInputType.Text,
      text: 'Question text (click to change)',
      config: {}
    });
    this.hasChanged = true;
  }

  public move(index: number, offset: number) {
    const target = index + offset;
    if (index != target && target >= 0 && target < this.entries.length) {
      this.arraymove(this.entries, index, target);
      this.hasChanged = true;
    }
  }

  public delete(index: number) {
    if (this.entries && index >= 0 && index < this.entries.length) {
      this.entries.splice(index, 1);
      this.hasChanged = true;
    }
  }

  public cancel() {
    return this.router.navigate(['/survey']);
  }

  public updateEntry: SurveyEntry = null;
  public updateOptions: string[];

  public editConfig(e: SurveyEntry, target: any) {
    this.updateEntry = e;
    this.updateOptions = [...(e.config.options ?? [])];
    const modal = this.modal.open(target, { size: 'lg' });
    return modal.result.then(
      res => {
        this.updateEntry.config.options = this.updateOptions;
        this.update();
      },
      reason => {}
    );
  }

  public addConfigOption() {
    this.updateOptions.push('New Option (click to change)');
  }

  public deleteConfigOption(index: number) {
    this.updateOptions.splice(index, 1);
  }

  public canEditConfig(e: SurveyEntry) {
    return e.type == SurveyEntryType.Input && (e.input == SurveyEntryInputType.Number || e.input == SurveyEntryInputType.Scale);
  }

  private arraymove(arr: any[], fromIndex: number, toIndex: number) {
    const element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
  }
}
